import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Margin } from 'styled-components-spacing';
import { Button, Col, Datepicker, Field, Radio, Reveal, Row, Select } from 'components';
import { formatDate } from 'helpers/date';
import DocumentGroup from './DocumentGroup';
import {
  EventCard,
  EventHeader,
  EventTitle,
  EmptyNote,
  LinkButton,
  NarrowInput,
  RadioRow,
} from '../Reports.styled';

/**
 * The events a Young Crew hosted in the reported year.
 *
 * An event either already lives in MyIPMA - then it is picked from the dropdown
 * and only the number of participants is asked for, because the system knows
 * the rest - or it does not, and it is typed in with the proof that it took
 * place attached to it.
 */
const EventsSection = ({
  events,
  registeredEventOptions,
  modeOfDeliveryOptions,
  proofExtensions,
  maxFileSize,
  errors,
  disabled,
  onChange,
  onAdd,
  onRemove,
  onAddProof,
  onRemoveStoredProof,
  onRemovePendingProof,
  registerCard,
}) => {
  const { t } = useTranslation();

  // An event already in the report keeps its place in the dropdown, everything
  // else that is taken is hidden so the same event cannot be added twice.
  const optionsFor = (event) =>
    registeredEventOptions.filter(
      (option) =>
        option.value === event.eventId ||
        !events.some((other) => other.key !== event.key && other.eventId === option.value),
    );

  const renderRegisteredChoice = (event, index) => (
    <Margin bottom="24">
      <Field
        id={`event-${event.key}-registered`}
        label={t('page.yc.reports.form.eventKind')}
        required
        error={errors[`events[${index}][eventId]`]}
      >
        <RadioRow>
          <Radio
            disabled={disabled}
            selected={event.registered}
            onClick={() => onChange(event.key, { registered: true, eventId: null })}
          >
            {t('page.yc.reports.form.eventRegistered')}
          </Radio>
          <Radio
            disabled={disabled}
            selected={!event.registered}
            onClick={() => onChange(event.key, { registered: false, eventId: null })}
          >
            {t('page.yc.reports.form.eventUnregistered')}
          </Radio>
        </RadioRow>
      </Field>
    </Margin>
  );

  const renderRegisteredEvent = (event, index) => {
    const options = optionsFor(event);
    const selected = options.find((option) => option.value === event.eventId) || '';

    return (
      <Reveal>
        <Margin bottom="24">
          <Field
            id={`event-${event.key}-event`}
            label={t('page.yc.reports.form.eventName')}
            description={
              selected
                ? t('page.yc.reports.form.eventDetails', {
                    date: selected.date ? formatDate(selected.date) : '-',
                    delivery: selected.modeOfDelivery || '-',
                  })
                : undefined
            }
            required
            error={errors[`events[${index}][eventId]`]}
          >
            {options.length ? (
              <NarrowInput>
                <Select
                  id={`event-${event.key}-event`}
                  type="text"
                  disabled={disabled}
                  placeholder={t('page.yc.reports.form.eventPlaceholder')}
                  value={selected}
                  options={options}
                  onChange={(option) =>
                    onChange(event.key, { eventId: option ? option.value : null })
                  }
                />
              </NarrowInput>
            ) : (
              <EmptyNote>{t('page.yc.reports.form.eventEmpty')}</EmptyNote>
            )}
          </Field>
        </Margin>
      </Reveal>
    );
  };

  const renderUnregisteredEvent = (event, index) => (
    <Reveal>
      <Row>
        <Margin bottom="24" as={Col} size={{ lg: 1 / 2 }}>
          <Field
            id={`event-${event.key}-name`}
            label={t('page.yc.reports.form.eventName')}
            required
            error={errors[`events[${index}][name]`]}
          >
            <input
              id={`event-${event.key}-name`}
              type="text"
              disabled={disabled}
              value={event.name || ''}
              placeholder={t('page.yc.reports.form.eventNamePlaceholder')}
              onChange={(e) => onChange(event.key, { name: e.target.value })}
            />
          </Field>
        </Margin>
        <Margin bottom="24" as={Col} size={{ lg: 1 / 2 }}>
          <Field
            id={`event-${event.key}-date`}
            label={t('page.yc.reports.form.eventDate')}
            required
            error={errors[`events[${index}][date]`]}
          >
            <Datepicker
              id={`event-${event.key}-date`}
              showYearDropdown
              scrollableYearDropdown
              dropdownMode="select"
              disabled={disabled}
              placeholderText={t('global.dateFromPlaceholder')}
              selected={event.date}
              onChange={(date) => onChange(event.key, { date })}
            />
          </Field>
        </Margin>
      </Row>
      <Row>
        <Margin bottom="24" as={Col} size={{ lg: 1 / 2 }}>
          <Field
            id={`event-${event.key}-mode`}
            label={t('page.yc.reports.form.eventModeOfDelivery')}
            required
            error={errors[`events[${index}][modeOfDelivery]`]}
          >
            <Select
              id={`event-${event.key}-mode`}
              type="text"
              disabled={disabled}
              placeholder={t('page.yc.reports.form.eventModePlaceholder')}
              value={
                modeOfDeliveryOptions.find((option) => option.value === event.modeOfDelivery) || ''
              }
              options={modeOfDeliveryOptions}
              onChange={(option) =>
                onChange(event.key, { modeOfDelivery: option ? option.value : null })
              }
            />
          </Field>
        </Margin>
      </Row>
      <Margin bottom="24">
        <DocumentGroup
          id={`event-${event.key}-proof`}
          label={t('page.yc.reports.form.eventProof')}
          description={t('page.yc.reports.form.eventProofHelp')}
          required
          disabled={disabled}
          error={errors[`events[${index}][proof]`]}
          extensions={proofExtensions}
          maxSize={maxFileSize}
          stored={event.proof}
          pending={event.newProof}
          onAdd={(file) => onAddProof(event.key, file)}
          onRemoveStored={(fileId) => onRemoveStoredProof(event.key, fileId)}
          onRemovePending={(fileIndex) => onRemovePendingProof(event.key, fileIndex)}
        />
      </Margin>
    </Reveal>
  );

  return (
    <>
      {errors.events ? (
        <Margin bottom="16">
          <Field id="events" error={errors.events}>
            <span />
          </Field>
        </Margin>
      ) : null}

      {events.length ? (
        events.map((event, index) => (
          <EventCard key={event.key} appear={event.isNew} ref={registerCard(event.key)}>
            <EventHeader>
              <EventTitle>{t('page.yc.reports.form.eventTitle', { number: index + 1 })}</EventTitle>
              {!disabled ? (
                <LinkButton type="button" onClick={() => onRemove(event.key)}>
                  {t('page.yc.reports.form.removeEvent')}
                </LinkButton>
              ) : null}
            </EventHeader>

            {renderRegisteredChoice(event, index)}
            {event.registered
              ? renderRegisteredEvent(event, index)
              : renderUnregisteredEvent(event, index)}

            <Row>
              <Margin as={Col} size={{ lg: 1 / 2 }}>
                <Field
                  id={`event-${event.key}-participants`}
                  label={t('page.yc.reports.form.eventParticipants')}
                  required
                  error={errors[`events[${index}][participants]`]}
                >
                  <input
                    id={`event-${event.key}-participants`}
                    type="number"
                    min="0"
                    disabled={disabled}
                    value={event.participants === null ? '' : event.participants}
                    placeholder={t('page.yc.reports.form.eventParticipantsPlaceholder')}
                    onChange={(e) =>
                      onChange(event.key, {
                        participants: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </Field>
              </Margin>
            </Row>
          </EventCard>
        ))
      ) : (
        <EmptyNote>
          {disabled
            ? t('page.yc.reports.form.eventsEmptyRead')
            : t('page.yc.reports.form.eventsEmpty')}
        </EmptyNote>
      )}

      {!disabled ? (
        <Margin top={events.length ? '0' : '20'}>
          <Row>
            <Col size={{ lg: 1 / 3 }}>
              <Button type="button" secondary onClick={onAdd}>
                {t('page.yc.reports.form.addEvent')}
              </Button>
            </Col>
          </Row>
        </Margin>
      ) : null}
    </>
  );
};

const eventShape = PropTypes.shape({
  key: PropTypes.string,
  id: PropTypes.number,
  isNew: PropTypes.bool,
  registered: PropTypes.bool,
  eventId: PropTypes.number,
  name: PropTypes.string,
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  modeOfDelivery: PropTypes.number,
  participants: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  proof: PropTypes.arrayOf(PropTypes.shape({})),
  newProof: PropTypes.arrayOf(PropTypes.shape({})),
});

EventsSection.propTypes = {
  events: PropTypes.arrayOf(eventShape),
  registeredEventOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
      date: PropTypes.string,
      modeOfDelivery: PropTypes.string,
    }),
  ),
  modeOfDeliveryOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.number, label: PropTypes.string }),
  ),
  proofExtensions: PropTypes.arrayOf(PropTypes.string),
  maxFileSize: PropTypes.number,
  errors: PropTypes.objectOf(PropTypes.string),
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  onAddProof: PropTypes.func,
  onRemoveStoredProof: PropTypes.func,
  onRemovePendingProof: PropTypes.func,
  registerCard: PropTypes.func,
};

EventsSection.defaultProps = {
  events: [],
  registeredEventOptions: [],
  modeOfDeliveryOptions: [],
  proofExtensions: [],
  maxFileSize: null,
  errors: {},
  disabled: false,
  onChange: () => undefined,
  onAdd: () => undefined,
  onRemove: () => undefined,
  onAddProof: () => undefined,
  onRemoveStoredProof: () => undefined,
  onRemovePendingProof: () => undefined,
  registerCard: () => undefined,
};

export default EventsSection;
